import { saveFiche } from "./api";
import {
  createCoffreFolder,
  deleteCoffreItem,
  listCoffre,
  loadFicheNotes,
  moveCoffreItem,
  moveNotesFile,
  renameCoffreItem,
  saveFicheNotes,
  type CoffreEntry,
} from "./coffre";
import type { CrmFiche } from "./types";

export const CRM_ROOT = "CRM clients";
export const INTERNE_ROOT = "CRM interne";
const LEGACY_CLIENT = "Mon CRM";
const LEGACY_INTERNE = "Interne";
const LEGACY_TASKS = "Tâches";
const NOTES_FILE = "notes-CRM.md";
const VISIT_KEY = "crm.coffre.visit.";

function oneHome(path: string, root: string): boolean {
  if (!path.startsWith(`${root}/`)) return false;
  return !path.slice(root.length + 1).includes("/");
}

export function isFicheHome(path: string): boolean {
  return oneHome(path, CRM_ROOT) || oneHome(path, LEGACY_CLIENT);
}

export function canonCoffrePath(path: string): string {
  const pairs: [string, string][] = [[LEGACY_CLIENT, CRM_ROOT], [LEGACY_INTERNE, INTERNE_ROOT]];
  for (const [from, to] of pairs) {
    if (path === from || path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}`;
  }
  return path;
}

export function ficheFolderName(name: string): string {
  const clean = name
    .replace(/[<>:"/\\|?*\u0000-\u001F]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\.+/, "")
    .replace(/[. ]+$/g, "");
  return clean.slice(0, 180) || "Fiche";
}

export function readCoffreVisit(ficheId: number): string | null {
  try {
    const value = localStorage.getItem(`${VISIT_KEY}${ficheId}`);
    return value === null ? null : canonCoffrePath(value);
  } catch {
    return null;
  }
}

export function writeCoffreVisit(ficheId: number, path: string): void {
  try {
    localStorage.setItem(`${VISIT_KEY}${ficheId}`, path);
  } catch {
    /* le dossier de la fiche reste l’accueil */
  }
}

export async function createFicheHome(name: string): Promise<string> {
  return createHome(CRM_ROOT, name);
}

export async function createInterneHome(name: string): Promise<string> {
  return createHome(INTERNE_ROOT, name);
}

async function createHome(root: string, name: string): Promise<string> {
  await ensureFolder("", root, 0);
  const rows = await listCoffre(root, true, 0, true);
  const taken = new Set(rows.map((row) => row.name.toLocaleLowerCase("fr")));
  return ensureFolder(root, spareName(ficheFolderName(name), taken), 0);
}

export async function renameFicheHome(path: string, nextName: string): Promise<string> {
  const root = [CRM_ROOT, LEGACY_CLIENT, INTERNE_ROOT, LEGACY_INTERNE].find((item) => oneHome(path, item));
  if (!root) return path;
  const wanted = ficheFolderName(nextName);
  const current = path.slice(root.length + 1);
  if (current === wanted) return path;
  const rows = await listCoffre(root, true, 0, true);
  if (rows.some((row) => row.path !== path && row.name.toLocaleLowerCase("fr") === wanted.toLocaleLowerCase("fr"))) {
    const where = root === INTERNE_ROOT || root === LEGACY_INTERNE ? "CRM interne" : "CRM clients";
    throw new Error(`Une fiche occupe déjà ce nom dans ${where}.`);
  }
  return renameCoffreItem(path, wanted, 0, true);
}

export function relocatePath(path: string, from: string, to: string): string {
  if (!path || !from || from === to) return path;
  if (path === from) return to;
  if (path.startsWith(`${from}/`)) return `${to}${path.slice(from.length)}`;
  return path;
}

/** Rassemble notes et tâches dans CRM clients / nom de la fiche, puis retire ces éléments du dossier client. */
export async function settleCrmHomes(fiches: CrmFiche[]): Promise<{ fiches: CrmFiche[]; error: string }> {
  const pending = fiches.filter((fiche) => fiche.scope !== "interne" && !fiche.coffre_path.startsWith(`${INTERNE_ROOT}/`) && !fiche.coffre_path.startsWith(`${LEGACY_INTERNE}/`) && (fiche.coffre_owner_id || 0) === 0 && fiche.coffre_path !== "" && !isFicheHome(fiche.coffre_path));
  if (pending.length === 0) return { fiches, error: "" };
  try {
    await ensureFolder("", CRM_ROOT, 0);
  } catch (reason: unknown) {
    return { fiches, error: reason instanceof Error ? reason.message : "CRM clients n’a pas pu être préparé." };
  }
  const reserved = new Set<string>();
  for (const fiche of fiches) {
    const homeRoot = [CRM_ROOT, LEGACY_CLIENT].find((item) => oneHome(fiche.coffre_path, item));
    if (homeRoot) reserved.add(fiche.coffre_path.slice(homeRoot.length + 1).toLocaleLowerCase("fr"));
  }
  const next = new Map<number, CrmFiche>();
  const errors: string[] = [];
  for (const fiche of pending) {
    try {
      next.set(fiche.id, await migrateFiche(fiche, reserved));
    } catch (reason: unknown) {
      const detail = reason instanceof Error ? reason.message : "enregistrement refusé";
      errors.push(`${fiche.name} : ${detail}`);
    }
  }
  return {
    fiches: fiches.map((fiche) => next.get(fiche.id) || fiche),
    error: errors.length > 0 ? `Certaines fiches n’ont pas rejoint CRM clients : ${errors[0]}` : "",
  };
}

async function migrateFiche(fiche: CrmFiche, taken: Set<string>): Promise<CrmFiche> {
  const folder = spareName(ficheFolderName(fiche.name), taken);
  const home = await ensureFolder(CRM_ROOT, folder, 0);
  const from = fiche.coffre_path;
  if (from && from !== home) {
    await bringNotes(from, home, fiche.coffre_owner_id || 0);
    await bringTasks(from, home, fiche.coffre_owner_id || 0);
  }
  if (fiche.coffre_path === home && (fiche.coffre_owner_id || 0) === 0) return fiche;
  return saveFiche({ ...fiche, coffre_path: home, coffre_owner_id: 0 });
}

async function bringNotes(from: string, home: string, ownerId: number): Promise<void> {
  const source = await loadFicheNotes(from, ownerId, true);
  if (!source.exists) return;
  if (source.notes.length === 0) {
    await deleteCoffreItem(`${from}/${NOTES_FILE}`, ownerId).catch(() => undefined);
    return;
  }
  const dest = await loadFicheNotes(home, 0, true);
  if (!dest.exists || dest.notes.length === 0) {
    await moveNotesFile({ path: from, ownerId }, { path: home, ownerId: 0 });
    return;
  }
  const merged = dest.notes.slice();
  for (const note of source.notes) {
    if (!merged.some((item) => item.heading === note.heading && item.body === note.body)) merged.push(note);
  }
  await saveFicheNotes(home, merged, true, 0);
  await deleteCoffreItem(`${from}/${NOTES_FILE}`, ownerId).catch(() => undefined);
}

async function bringTasks(from: string, home: string, ownerId: number): Promise<void> {
  const rows = await listCoffre(from, false, ownerId, true);
  const folder = rows.find((entry) => entry.kind === "folder" && entry.name === LEGACY_TASKS);
  if (!folder) return;
  const children = await listCoffre(folder.path, false, ownerId, true);
  const dest = await listCoffre(home, false, 0, true);
  const names = new Set(dest.map((entry) => entry.name.toLocaleLowerCase("fr")));
  for (const child of children) {
    if (child.name.toLowerCase() === NOTES_FILE.toLowerCase()) {
      await bringNotes(folder.path, home, ownerId);
      continue;
    }
    await placeInto(child, home, names, ownerId);
  }
  const left = await listCoffre(folder.path, false, ownerId, true);
  if (left.length === 0) await deleteCoffreItem(folder.path, ownerId).catch(() => undefined);
}

async function placeInto(entry: CoffreEntry, dest: string, names: Set<string>, ownerId: number): Promise<void> {
  let path = entry.path;
  let label = entry.name;
  if (names.has(label.toLocaleLowerCase("fr"))) {
    label = spareFile(label, names);
    path = await renameCoffreItem(path, label, ownerId);
  }
  await moveCoffreItem(path, dest, ownerId);
  names.add(label.toLocaleLowerCase("fr"));
}

async function ensureFolder(parent: string, name: string, ownerId: number): Promise<string> {
  const rows = await listCoffre(parent, true, ownerId, true);
  const found = rows.find((entry) => entry.kind === "folder" && entry.name === name);
  if (found) return found.path;
  return createCoffreFolder(parent, name, ownerId);
}

function spareName(base: string, taken: Set<string>): string {
  if (!taken.has(base.toLocaleLowerCase("fr"))) {
    taken.add(base.toLocaleLowerCase("fr"));
    return base;
  }
  let index = 2;
  let name = `${base} ${index}`;
  while (taken.has(name.toLocaleLowerCase("fr"))) {
    index += 1;
    name = `${base} ${index}`;
  }
  taken.add(name.toLocaleLowerCase("fr"));
  return name;
}

function spareFile(name: string, names: Set<string>): string {
  const dot = name.lastIndexOf(".");
  const stem = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let index = 2;
  let next = `${stem} ${index}${ext}`;
  while (names.has(next.toLocaleLowerCase("fr"))) {
    index += 1;
    next = `${stem} ${index}${ext}`;
  }
  return next;
}
